// src/components/Auth.js
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function Auth() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login, signup } = useAuth();

  const mode = searchParams.get("mode") === "signup" ? "signup" : "login";
  const roleParam = searchParams.get("role");

  const role = useMemo(() => {
    if (roleParam === "tasker") return "tasker";
    if (roleParam === "admin") return "admin";
    return "customer";
  }, [roleParam]);

  const isSignup = mode === "signup";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const pageTitle = useMemo(() => {
    if (role === "tasker") return isSignup ? "Tasker Sign Up" : "Tasker Login";
    if (role === "admin") return isSignup ? "Admin Sign Up" : "Admin Login";
    return isSignup ? "Sign Up" : "Login";
  }, [role, isSignup]);

  const switchModeLink = `/auth?role=${role}&mode=${isSignup ? "login" : "signup"}`;

  const routeByRole = (userRole) => {
    if (userRole === "customer") return navigate("/customer/home");
    if (userRole === "tasker") return navigate("/tasker/dashboard");
    if (userRole === "admin") return navigate("/admin/dashboard");
    navigate("/");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (isSignup) {
        await signup({ email, password, role });
        routeByRole(role);
      } else {
        const res = await login({ email, password, expectedRole: role });
        routeByRole(res.profile.role);
      }
    } catch (err) {
      setError(err?.message || "Authentication failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-start justify-center px-4 py-10">
      <div className="w-full max-w-2xl">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-slate-600 mb-8 hover:text-slate-900"
        >
          <ArrowLeft size={20} />
          Back
        </button>

        <div className="bg-white rounded-3xl shadow-lg max-w-xl mx-auto px-9 py-10">
          <h2 className="text-center text-[#1E3A8A] mb-10">{pageTitle}</h2>

          <form onSubmit={handleSubmit} className="space-y-7">
            <div>
              <label className="block text-gray-800 mb-3">Phone / Email</label>
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter phone or email"
                className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#006C35]"
                required
              />
            </div>

            <div>
              <label className="block text-gray-800 mb-3">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="w-full px-5 py-4 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#006C35]"
                required
              />
            </div>

            {error ? (
              <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-xl p-3">
                {error}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-[#007A33] text-white py-4 rounded-xl hover:bg-[#006C35] transition-colors ${
                loading ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {loading ? "Please wait..." : isSignup ? "Sign Up" : "Login"}
            </button>
          </form>

          <div className="mt-10">
            <div className="flex items-center gap-4">
              <div className="flex-1 border-t border-gray-300" />
              <span className="text-[#6B7280]">OR</span>
              <div className="flex-1 border-t border-gray-300" />
            </div>

            <button
              type="button"
              className="w-full mt-8 bg-white border-2 border-[#1E3A8A] text-[#1E3A8A] py-4 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Continue with SMS OTP
            </button>
          </div>

          <div className="mt-10 pt-8 border-t border-gray-200 text-center">
            <p className="text-gray-700 mb-2">
              {isSignup ? "Already have an account?" : "Don't have an account?"}
            </p>

            <Link
              to={switchModeLink}
              className="text-[#007A33] font-semibold hover:underline"
            >
              {isSignup ? "Login" : "Sign Up"}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}